import _ from "lodash";
import { produce } from "immer";
import flattenDeep from "lodash/flattenDeep";

import { DateFormat, EmailType, RequestStatus, SystemCode } from "../constants";
import { DateHelper, StringHelper } from "../helper";
import { useUser } from "./use-user";
import { useMail } from "./use-mail";
import { useSoftwares } from "./use-softwares";
import { IApprovalHistory, StepItem } from "../components";
import { isProduction } from "../services/serviceHelper";
import moment from "moment";

export interface IAPItem {
  title: string;
  status: RequestStatus.approved | RequestStatus.rejected | RequestStatus.canceled | "";
  name: string;
  email: string;
  date: string;
  reason: string;
  requestStatus: RequestStatus;
  isRequired: boolean;
  roles: string[];
  history?: any[];
}

export type IAPList = IAPItem[][];
type StepStatus = "finish" | "wait" | "process" | "error" | undefined;
// type AvailableStepItem =
//   | "empty"
//   | "initialize"
//   | "eApprovedByITLead"
//   | "eApprovedByITHead"
//   | "eAssignee"
//   | "eMedicalStaff"
//   | "approvedBy";
interface IEmails {
  approved: EmailType;
  rejected: EmailType;
  completed: EmailType;
  canceled?: EmailType;
}
interface ISendMailOptions {
  type: RequestStatus.approved | RequestStatus.rejected;
  toPath: string;
  prevStep?: IAPItem;
}

interface ProcessParams {
  current: number;
  eRequestStatus: string;
  steps: any[][];
  isVOS?: boolean;
}

const getActionText = (status: string, index: number, length: number) => {
  if (index === 0) return "Ngày tạo: ";
  if (status === RequestStatus.rejected) return "Ngày từ chối: ";
  if (status === RequestStatus.canceled) return "Ngày huỷ: ";
  return index === length - 1 ? "Ngày hoàn tất: " : "Ngày duyệt: ";
};
const isRejected = (status: string) => status === RequestStatus.rejected;
const isApproved = (status: string) => status === RequestStatus.approved;
const stepsParser = (steps: IAPList | string) => {
  if (!(typeof steps === "string")) return steps;
  return JSON.parse(steps);
};

export const useSteps = (emails?: IEmails) => {
  const { checkCurrentUser, user } = useUser();
  const { sendMail } = useMail();
  const { getITLeadITHeadAssignee } = useSoftwares();

  const approve = (steps: IAPList = []) => {
    let stepNumber = 0;
    let stepStatus: StepStatus = "finish";
    let userStepNumber: number = 0;
    let userStep: IAPItem;
    let isApprovedUser = false;
    let status: { label: string; flag: string };

    const flattedSteps = _.flattenDepth(steps);
    const hasCanceled = flattedSteps.some((e) => e.status === RequestStatus.canceled);

    for (var i = steps.length - 1; i >= 0; i--) {
      const step = steps[i];
      const requiredItems = step.filter((item) => item.isRequired);
      const approvedItems = step.filter((item) => {
        return item.status && item.isRequired;
      });
      const hasRejectedStatus = step.find((item) => isRejected(item.status));
      const isWait = requiredItems.length !== approvedItems.length;

      if (approvedItems.length !== 0) {
        if (hasCanceled) {
          stepNumber = 0;
        } else {
          stepNumber = i;
        }
        stepStatus = hasRejectedStatus ? "error" : isWait ? "wait" : "finish";
        break;
      }
    }
    const userStepList = [].reduce((acc: any[], current, index) => {
      for (let item of current) {
        if (checkCurrentUser(item.email) && index !== 0) {
          acc.push({ ...item, stepNumber: index });
        }
      }
      return acc;
    }, []);
    const userStepNotApproved = userStepList.find((item) => !item.status);
    if (userStepNotApproved) {
      userStepNumber = userStepNotApproved?.stepNumber;
      const condition1 = stepStatus === "finish" && stepNumber + 1 === userStepNumber;
      const condition2 = ["finish", "wait"].includes(stepStatus) && stepNumber === userStepNumber;
      isApprovedUser = (condition1 || condition2) && !hasCanceled;
      userStep = _.omit(userStepNotApproved, ["stepNumber"]) as IAPItem;
    } else {
      isApprovedUser = false;
      userStepNumber = userStepList[0]?.stepNumber;
      userStep = _.omit(userStepList[0], ["stepNumber"]) as IAPItem;
    }

    if (hasCanceled) {
      status = { label: "Đã huỷ", flag: "danger" };
    } else if (stepStatus === "error") {
      status = { label: "Đã từ chối", flag: "danger" };
    } else if (stepNumber === steps.length - 1) {
      status = { label: "Đã hoàn tất", flag: "success" };
    } else if (userStepNotApproved || userStepList.length === 0) {
      status = { label: "Đang đợi duyệt", flag: "warning" };
    } else {
      status = { label: "Đã duyệt", flag: "success" };
    }

    const isCompleted = stepNumber === steps.length - 1;
    const nextSteps: IAPItem[] = isCompleted ? steps[0] : steps[stepNumber + 1];
    const canEdit = stepNumber === 0 && checkCurrentUser(steps[0]?.[0]?.email) && !hasCanceled;

    return {
      stepNumber,
      stepStatus,
      userStepNumber,
      userStep,
      isApprovedUser,
      status,
      nextSteps,
      canEdit,
    };
  };

  const process = ({ current, eRequestStatus, steps, isVOS }: ProcessParams) => {
    const isRejected = eRequestStatus === RequestStatus.rejected;
    const isCancelled = eRequestStatus === RequestStatus.canceled;
    const isCompleted = eRequestStatus === RequestStatus.completed;
    const isRevoked = eRequestStatus === RequestStatus.revoked;

    const nextStep = steps[current + 1] as any[] | undefined;
    const isFinishedProcess = nextStep === undefined || isCancelled || isCompleted;
    const flattedSteps = flattenDeep(steps);

    const getStatus = () => {
      const userStepItems = flattedSteps.filter((i, idx) => {
        return idx !== 0 && checkCurrentUser(i.email);
      });
      const isApproved = userStepItems.every((i) => i.isApproved);

      if (isRejected) {
        return { label: "Đã từ chối", flag: "danger", isApproved: false };
      }
      if (isCancelled) {
        return { label: "Đã huỷ", flag: "danger", isApproved: false };
      }
      if (isCompleted) {
        return { label: "Đã hoàn tất", flag: "success", isApproved: false };
      }

      if (isRevoked) {
        return { label: "Thu hồi", flag: "grey", isApproved: false };
      }

      if (isApproved && userStepItems.length !== 0) {
        return { label: "Đã duyệt", flag: "success", isApproved: true };
      }
      return { label: "Đang đợi duyệt", flag: "warning", isApproved: false };
    };

    if (isFinishedProcess) {
      return {
        canEdit: false,
        canApprove: false,
        canCancel: isVOS ? false : !isCancelled,
        canRecover: false,
        status: getStatus(),
      };
    }

    const userStepItem = nextStep.find((item) => checkCurrentUser(item.email));
    const isInit = Boolean(steps[0].find((i) => checkCurrentUser(i.email)));
    const isApproved = userStepItem?.isApproved === true;

    return {
      canApprove: userStepItem && !isApproved && !isRejected && !isCancelled && !isCompleted,
      canEdit: (isVOS ? false : true) && current === 0 && isInit,
      canCancel: (isVOS ? false : true) && !isRejected && isInit,
      // canRecover: isVOS && isInit && eRequestStatus === RequestStatus.submitted,
      canRecover: false,
      status: getStatus(),
    };
  };

  const getCurrent = (newStep: any[], current: number): number => {
    const requiredStepItems = newStep.filter((s) => s.isRequired);
    const approvedStepItems = newStep.filter((s) => s.isApproved);
    if (requiredStepItems.length === approvedStepItems.length) {
      return current + 1;
    }
    return current;
  };

  const cancel = (steps: IAPList = [], reqStatus: RequestStatus) => {
    const initialStep = steps[0];
    const canCancel =
      checkCurrentUser(initialStep?.[0].email) &&
      reqStatus !== RequestStatus.rejected &&
      reqStatus !== RequestStatus.canceled;

    const getNewApprovalProcess = (note?: string) => {
      return produce(steps, (draft) => {
        if (draft) {
          draft[0][0].status = RequestStatus.canceled;
          draft[0][0].history?.push({
            status: RequestStatus.canceled,
            createdDate: DateHelper.getIsoStrNow(),
            note: note ?? "",
          });
        }
      });
    };

    const cancelSendMail = ({ toPath, eMailType }: any) => {
      const flattedSteps = flattenDeep(steps);
      const approvedStepItems = flattedSteps.filter((stepItem, idx) => stepItem.status && idx !== 0);
      const { stepNumber } = approve(steps);
      const nextStepItems = flattenDeep(steps.at(stepNumber + 1)) ?? [];
      const stepItemList = [...approvedStepItems, ...nextStepItems];

      Promise.all(
        stepItemList.map(({ name, email }) =>
          sendMail({
            mailTo: { name, email, action: undefined },
            toPath,
            eMailType,
            created: {
              byName: initialStep?.[0].name,
              dateTime: moment().format(DateFormat.DD_MM_YYYY_HH_MM),
            },
          }),
        ),
      );
    };

    return { canCancel, getNewApprovalProcess, cancelSendMail };
  };

  const sendMailAP = (stepList: IAPList | string = [], { toPath = "", type, prevStep }: ISendMailOptions) => {
    const steps = stepsParser(stepList);
    const { stepNumber, stepStatus, nextSteps } = approve(steps);
    const initialStep = steps[0][0];

    const send = ({ name, email, action, eMailType }: any) => {
      const createdDateTime = DateHelper.formatDateToString(initialStep.date, {
        format: DateFormat.DD_MM_YYYY_HH_MM,
      });
      return sendMail({
        mailTo: { name, email, action },
        toPath,
        eMailType,
        created: { byName: initialStep.name, dateTime: createdDateTime },
      });
    };

    if (isRejected(type) && (prevStep?.isRequired ?? true)) {
      Promise.all([initialStep].map(({ name, email }) => send({ name, email, eMailType: emails?.rejected })));
    } else if (stepStatus === "finish" && isApproved(type)) {
      const isCompleted = stepNumber === steps.length - 1;
      const eMailType = isCompleted ? emails?.completed : emails?.approved;
      const lastStepItem = steps[steps.length - 1][0];
      const action = isCompleted ? lastStepItem.reason : undefined;
      Promise.all(
        nextSteps.map(({ name, email }) => {
          return send({ name, email, eMailType, action });
        }),
      );
    }
  };

  const getStepItems = (steps: IAPList = []): StepItem[] => {
    return steps.map((step, idx, arr) => {
      return step.map((stepItem) => {
        const { title, status, date: dateItem } = stepItem;
        const date = DateHelper.formatDateToString(dateItem, {
          format: DateFormat.DD_MM_YYYY_HH_MM,
        });
        const actionText = getActionText(status, idx, arr.length);
        return { title, stepContent: { ...stepItem, date, actionText } };
      });
    });
  };

  const getApprovalHistory = (steps: IAPList = []): IApprovalHistory[] => {
    const stepList = _.flattenDeep(steps);

    return stepList.reduce((acc: IApprovalHistory[], current, index) => {
      const { title, name, status, date: dateItem, reason } = current;
      const date = DateHelper.formatDateToString(dateItem, {
        format: DateFormat.DD_MM_YYYY_HH_MM,
      });
      if (status && index !== 0) {
        acc.push({ name: `${title}, ${name}`, status, reason, date });
      }
      return acc;
    }, []);
  };

  const getHistories = (eSteps: any[] = []): IApprovalHistory[] => {
    const mappedHistories = eSteps.map((step, index) => {
      return step.map((stepItem: any) => {
        return stepItem.histories.map((history: any) => ({
          ...history,
          name: index === 0 ? stepItem.name : `${stepItem.title}, ${stepItem.name}`,
          date: DateHelper.formatDateToString(history.updatedDateTime, {
            format: DateFormat.DD_MM_YYYY_HH_MM,
          }),
        }));
      });
    });
    return flattenDeep(mappedHistories).sort(
      (a, b) => new Date(a.updatedDateTime).getTime() - new Date(b.updatedDateTime).getTime(),
    );
  };

  const getAvailableStep = (eSystemCode: SystemCode, eApprovedBy?: string): any => {
    const { eApprovedByITLead, eApprovedByITHead, eAssignee } = getITLeadITHeadAssignee(eSystemCode);
    return {
      empty: {
        title: "",
        name: "",
        email: "",
        isRequired: true,
        roles: ["user"],
        isApproved: false,
        histories: [],
      },
      initialize: {
        title: "Khởi tạo",
        name: user.current.name,
        email: user.current.username,
        isRequired: true,
        roles: ["user"],
        isApproved: true,
        histories: [
          {
            label: "Ngày tạo",
            status: RequestStatus.created,
            updatedDateTime: DateHelper.getIsoStrNow(),
            reason: "",
          },
        ],
      },
      eApprovedByITLead: {
        title: "",
        name: StringHelper.verticalBarSplit(eApprovedByITLead)[1],
        email: StringHelper.verticalBarSplit(eApprovedByITLead)[0],
        isRequired: true,
        roles: ["user"],
        isApproved: false,
        histories: [],
      },
      eApprovedByITHead: {
        title: "",
        name: StringHelper.verticalBarSplit(eApprovedByITHead)[1],
        email: StringHelper.verticalBarSplit(eApprovedByITHead)[0],
        isRequired: true,
        roles: ["user"],
        isApproved: false,
        histories: [],
      },
      eAssignee: {
        title: "",
        name: StringHelper.verticalBarSplit(eAssignee)[1],
        email: StringHelper.verticalBarSplit(eAssignee)[0],
        isRequired: true,
        roles: ["user"],
        isApproved: false,
        histories: [],
      },
      eMedicalStaff: {
        title: "Cán bộ y tế",
        name: "Phan Xuân Nam",
        email: isProduction ? "nampx@viags.vn" : "canbo1@viags.vn",
        isRequired: true,
        roles: ["doctor"],
        histories: [],
      },
      approvedBy: {
        title: "",
        name: StringHelper.verticalBarSplit(eApprovedBy)[1],
        email: StringHelper.verticalBarSplit(eApprovedBy)[0],
        isRequired: true,
        roles: ["user"],
        isApproved: false,
        histories: [],
      },
    };
  };

  const updateSteps = (steps: IAPList = [], newStep: IAPItem) => {
    const { userStepNumber: idx, userStep } = approve(steps);
    return produce(steps, (draft) => {
      const step = draft[idx];
      const itemIndex = step.findIndex((e) => {
        return !e.status && userStep.email === e.email;
      });
      step[itemIndex] = { ...newStep, date: DateHelper.getIsoStrNow() };
    });
  };

  const getNewSteps = (
    steps: any[][],
    stepIndex: number,
    action: "approve" | "reject" | "cancel" | "recover",
    reason?: string,
  ) => {
    const history: any = {
      updatedDateTime: DateHelper.getIsoStrNow(),
    };
    if (action === "approve") {
      history["label"] = "Ngày duyệt";
      history["status"] = RequestStatus.approved;
      history["reason"] = reason || "";
    }
    if (action === "reject") {
      history["label"] = "Ngày từ chối";
      history["status"] = RequestStatus.rejected;
      history["reason"] = reason || "";
    }
    if (action === "cancel") {
      history["label"] = "Ngày huỷ bỏ";
      history["status"] = RequestStatus.canceled;
      history["reason"] = reason || "";
    }

    const newSteps: any = produce(steps, (draft: any) => {
      const step = draft[stepIndex];
      const stepItem = step.find((item: any) => checkCurrentUser(item.email));
      stepItem.isApproved = true;
      stepItem.histories = [...stepItem.histories, history];
    });
    return newSteps;
  };

  const notify = ({
    steps,
    toPath,
    eRequestStatus,
    current,
    requestUser,
    reason,
    aflTypeLabel,
  }: {
    steps: any[][];
    current: number;
    toPath: string;
    eRequestStatus: string;
    requestUser: { name: string; email: string };
    reason: string;
    aflTypeLabel?: string;
  }) => {
    const initStepItem = steps[0][0];
    const send = ({ name, email, action, eMailType }: any) => {
      const createdDateTime = DateHelper.formatDateToString(initStepItem.histories[0].updatedDateTime, {
        format: DateFormat.DD_MM_YYYY_HH_MM,
      });
      return sendMail({
        mailTo: { name, email, action },
        toPath,
        eMailType,
        created: { byName: initStepItem.name, dateTime: createdDateTime },
        aflTypeLabel: aflTypeLabel,
      });
    };

    if (eRequestStatus === RequestStatus.rejected) {
      const users = [initStepItem];
      Promise.all(users.map((user) => send({ ...user, eMailType: emails?.rejected, action: reason })));
    } else if (eRequestStatus === RequestStatus.canceled) {
      const users = [...flattenDeep(steps[current]), ...(requestUser.email ? [requestUser] : [])];
      Promise.all(users.map((user) => send({ ...user, eMailType: emails?.canceled, action: reason })));
    } else if (eRequestStatus === RequestStatus.completed) {
      const users = [initStepItem, ...(requestUser.email ? [requestUser] : [])];
      Promise.all(users.map((user) => send({ ...user, eMailType: emails?.completed, action: reason })));
    } else {
      const users = steps[current + 1];
      Promise.all(users.map((user) => send({ ...user, eMailType: emails?.approved })));
    }
  };

  return {
    approve,
    cancel,
    sendMailAP,
    getStepItems,
    getApprovalHistory,
    getAvailableStep,
    updateSteps,
    process,
    getCurrent,
    getNewSteps,
    notify,
    getHistories,
  };
};
